import { AgentToolCallStatus, reasoningOf, toolRegistry, } from '@debbie/common/dist/agents/index.js';
import { proposalTitle } from '@debbie/cortex/dist/agents/index.js';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import Badge from '../Badge.js';
import Button from '../Button.js';
import Modal from '../Modal.js';
import Well from '../Well.js';
import InteractionPreview from './InteractionPreview.js';
import ProposalBody from './ProposalBody.js';
const Card = styled.div `
  padding: ${({ $embedded }) => ($embedded ? 0 : `${THEME.GRID_SPACING}px 0`)};
  /* Separates stacked cards; an embedded card stands alone. */
  border-top: ${({ $embedded }) => ($embedded ? 'none' : '1px solid #ececef')};

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
  &:last-child {
    padding-bottom: 0;
  }
`;
const ProposalContent = styled(Well) `
  ${({ $embedded }) => $embedded &&
    `
    padding: 0;
    border: none;
    background: transparent;
  `}
`;
const HeaderRow = styled.div `
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;
const Title = styled.div `
  font-weight: 700;
  font-size: 0.95em;
  color: ${THEME.COLOR_PRIMARY};
  flex: 0 0 auto;
`;
const HeaderTime = styled.span `
  color: ${THEME.COLOR_GREY};
  font-size: 0.8em;
  flex: 1 1 auto;
`;
const ReasoningBlock = styled.div `
  margin-top: ${THEME.GRID_SPACING}px;
  margin-bottom: ${THEME.GRID_SPACING}px;
  padding: 8px 10px;
  background: #eee;
  border-radius: 3px;
`;
const ReasoningLabel = styled.div `
  font-size: 0.7em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${THEME.COLOR_ACCENT};
  margin-bottom: 2px;
`;
const ReasoningText = styled.div `
  color: ${THEME.COLOR_PRIMARY};
  font-size: 0.85em;
  line-height: 1.45;
`;
const Actions = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: ${THEME.GRID_SPACING}px;
`;
const ActionsRight = styled.div `
  margin-left: auto;
  display: flex;
  gap: 8px;
`;
const ErrorRow = styled.div `
  margin-top: 10px;
  font-size: 0.8em;
  color: ${THEME.DANGEROUS};
  white-space: pre-wrap;
`;
const STATUS_COLOR = {
    [AgentToolCallStatus.Proposed]: THEME.COLOR_ACCENT,
    [AgentToolCallStatus.Executing]: THEME.COLOR_ACCENT,
    [AgentToolCallStatus.Executed]: THEME.SUCCESS,
    [AgentToolCallStatus.Rejected]: THEME.DANGEROUS,
    [AgentToolCallStatus.Failed]: THEME.DANGEROUS,
    [AgentToolCallStatus.Stale]: THEME.COLOR_GREY,
    [AgentToolCallStatus.Superseded]: THEME.COLOR_GREY,
    [AgentToolCallStatus.Dismissed]: THEME.COLOR_GREY,
};
export const ProposalStatusBadge = ({ status, }) => {
    const lang = useSelector((s) => s.lang);
    const labels = {
        [AgentToolCallStatus.Proposed]: lang.agentProposals.pending,
        [AgentToolCallStatus.Executing]: lang.agentProposals.executing,
        [AgentToolCallStatus.Executed]: lang.agentProposals.approved,
        [AgentToolCallStatus.Rejected]: lang.agentProposals.rejected,
        [AgentToolCallStatus.Failed]: lang.agentProposals.failed,
        [AgentToolCallStatus.Stale]: lang.agentProposals.stale,
        [AgentToolCallStatus.Superseded]: lang.agentProposals.superseded,
        [AgentToolCallStatus.Dismissed]: lang.agentProposals.dismissed,
    };
    return React.createElement(Badge, { theme: STATUS_COLOR[status] }, labels[status]);
};
const ProposalCard = ({ proposal, caseId, inFlight = false, approveLoading = false, rejectLoading = false, retryLoading = false, dismissLoading = false, onApprove, onReject, onRetry, onDismiss, previewEnabled = true, showValidationErrors = true, embedded = false, showProposedInput = false, }) => {
    const lang = useSelector((s) => s.lang);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [editsState, setEditsState] = useState(null);
    const reasoning = reasoningOf(proposal.proposedInput);
    const isPending = proposal.status === AgentToolCallStatus.Proposed;
    const isFailed = proposal.status === AgentToolCallStatus.Failed;
    const isEditable = (isPending && onApprove != null) || (isFailed && onRetry != null);
    const displayInput = showProposedInput
        ? proposal.proposedInput
        : (proposal.appliedInput ?? proposal.proposedInput);
    const canPreview = toolRegistry[proposal.toolName]?.previewable === true && previewEnabled;
    return (React.createElement(Card, { $embedded: embedded },
        !embedded && (React.createElement(HeaderRow, null,
            React.createElement(Title, null, proposalTitle(proposal.toolName, displayInput, lang)),
            React.createElement(HeaderTime, { title: moment(proposal.proposedAt).format('LLL') }, moment(proposal.proposedAt).fromNow()),
            React.createElement(ProposalStatusBadge, { status: proposal.status }))),
        React.createElement(ProposalContent, { $embedded: embedded },
            React.createElement(ProposalBody, { toolName: proposal.toolName, proposedInput: displayInput, caseId: caseId, showValidationErrors: showValidationErrors, onEditsChange: isEditable ? setEditsState : undefined }),
            proposal.errorMessage && (React.createElement(ErrorRow, null,
                React.createElement("strong", null,
                    lang.agentProposals.errorMessage,
                    ": "),
                proposal.errorMessage))),
        reasoning && (React.createElement(ReasoningBlock, null,
            React.createElement(ReasoningLabel, null, lang.agentProposals.reasoning),
            React.createElement(ReasoningText, null, reasoning))),
        isPending && onApprove && onReject && (React.createElement(Actions, null,
            canPreview && (React.createElement(Button, { size: "small", theme: "secondary", disabled: inFlight, onClick: () => setPreviewOpen(true) }, lang.interactions.preview)),
            React.createElement(ActionsRight, null,
                React.createElement(Button, { size: "small", theme: "secondary", loading: inFlight && rejectLoading, disabled: inFlight, onClick: () => onReject(null) }, lang.agentProposals.reject),
                React.createElement(Button, { size: "small", loading: inFlight && approveLoading, disabled: inFlight || editsState?.isValid === false, onClick: () => onApprove(editsState?.edits ?? null) }, lang.agentProposals.approve)))),
        isFailed && (onRetry || onDismiss) && (React.createElement(Actions, null,
            canPreview && (React.createElement(Button, { size: "small", theme: "secondary", disabled: inFlight, onClick: () => setPreviewOpen(true) }, lang.interactions.preview)),
            React.createElement(ActionsRight, null,
                onDismiss && (React.createElement(Button, { size: "small", theme: "secondary", loading: inFlight && dismissLoading, disabled: inFlight, onClick: onDismiss }, lang.agentProposals.dismiss)),
                onRetry && (React.createElement(Button, { size: "small", loading: inFlight && retryLoading, disabled: inFlight || editsState?.isValid === false, onClick: () => onRetry(editsState?.edits ?? null) }, lang.agentProposals.retry))))),
        previewOpen && (React.createElement(Modal, { header: lang.interactions.preview, onToggle: () => setPreviewOpen(false), maxWidth: "900px" },
            React.createElement(InteractionPreview, { proposalId: proposal.id, edits: editsState?.edits ?? null })))));
};
export default ProposalCard;
//# sourceMappingURL=ProposalCard.js.map